import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PageResponse,
  PublicOrganizationDetail,
  PublicOrganizationMapFilters,
  PublicOrganizationMapPoint,
  PublicOrganizationMapSearchFilters,
  PublicOrganizationMapStatistics,
  PublicProfileMapFilters,
  PublicProfileMapPoint,
  PublicProfileMapSearchFilters,
  PublicProfileMapStatistics,
} from '../../../core/models';

function toHttpParams<T extends object>(filters: T): HttpParams {
  let params = new HttpParams();
  Object.entries(filters as Record<string, string | number | undefined>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params = params.set(key, value);
    }
  });
  return params;
}

/**
 * Cartographie publique (profile-service, `GET /public/organizations/**` et `GET /public/profiles/**`).
 * Endpoints sans JWT, dédiés au visiteur non connecté — distincts de NetworkService
 * (`/profiles/search`, `/profiles/public/{id}`) qui exige une authentification malgré son nom.
 *
 * `/public/profiles/map` (et `/map/filters`, `/map/statistics`) est le point d'entrée à privilégier
 * pour la page cartographie publique : il combine organisations et jeunes anonymisés en un seul appel
 * (voir `PublicProfileMapPoint`). `/public/organizations/map` reste disponible pour un écran qui
 * voudrait volontairement une carte strictement organisationnelle.
 */
@Injectable({ providedIn: 'root' })
export class PublicOrganizationsMapService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/profile-service/api/v1/public/organizations`;
  private readonly profilesBaseUrl = `${environment.apiUrl}/profile-service/api/v1/public/profiles`;

  filters(): Observable<PublicOrganizationMapFilters> {
    return this.http.get<PublicOrganizationMapFilters>(`${this.baseUrl}/map/filters`);
  }

  statistics(): Observable<PublicOrganizationMapStatistics> {
    return this.http.get<PublicOrganizationMapStatistics>(`${this.baseUrl}/map/statistics`);
  }

  map(filters: PublicOrganizationMapSearchFilters = {}): Observable<PageResponse<PublicOrganizationMapPoint>> {
    return this.http.get<PageResponse<PublicOrganizationMapPoint>>(`${this.baseUrl}/map`, { params: toHttpParams(filters) });
  }

  detail(organizationId: string): Observable<PublicOrganizationDetail> {
    return this.http.get<PublicOrganizationDetail>(`${this.baseUrl}/${organizationId}`);
  }

  /** Filtres réellement disponibles sur la carte combinée (inclut `accountTypes`, donc YOUNG si des jeunes publics existent). */
  profileFilters(): Observable<PublicProfileMapFilters> {
    return this.http.get<PublicProfileMapFilters>(`${this.profilesBaseUrl}/map/filters`);
  }

  profileStatistics(): Observable<PublicProfileMapStatistics> {
    return this.http.get<PublicProfileMapStatistics>(`${this.profilesBaseUrl}/map/statistics`);
  }

  /** Carte combinée organisations + jeunes anonymisés — voir `PublicProfileMapPoint`. */
  profileMap(filters: PublicProfileMapSearchFilters = {}): Observable<PageResponse<PublicProfileMapPoint>> {
    return this.http.get<PageResponse<PublicProfileMapPoint>>(`${this.profilesBaseUrl}/map`, { params: toHttpParams(filters) });
  }
}
